import { observer } from "mobx-react";
import {
  Button,
  Checkbox,
  HStack,
  Stack,
  Text,
  Box,
  RadioGroup,
  Alert,
  NativeSelect,
  Field,
  Input,
  Spacer,
  Progress,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import mapboxgl from "mapbox-gl";
import { mapActive, mapUtils } from "../../../../../../map/utils/map-utils";
import { Project } from "../../../../../../project/models/Project";
import { ProjectApi } from "../../../../../../project/services/ProjectApi";
import { Campaign } from "../../../../../../project/models/Campaign";
import { OptionalScope } from "../../../../../../shared/models/OptionalScope";
import { Feature, FeatureCollection, Point, Position } from "geojson";
import { copyNodesMapSource } from "../../../../../../map/sources/copyNodesMapSource";
import { OsaNode } from "../../../../../../osaway/models/OsaNode";
import { GeoJsonNodeProps } from "../../../../../../osaway/models/CustomGeoJsonProperties";
import { OsaWayApi } from "../../../../../../osaway/services/OsaWayApi";
import { OsaNodeApi } from "../../../../../../osaway/services/OsaNodeApi";
import { useStores } from "../../../../../../shared/stores/RootStore";
import { toast } from "../../../../../../shared/views/CustomToaster";
import { CustomDialog } from "../../../../../../shared/views/CustomDialog";
import { compileAllPages } from "../../../../../../shared/utils/http-utils";
import { JobStateApi } from "../../../../../../shared/services/JobStateApi";
import { ProgressState } from "../../../../../../shared/models/JobState";
import { OsaWay } from "../../../../../../osaway/models/OsaWay";
import { CampaignApi } from "../../../../../../project/services/CampaignApi";

const i18prefix = "map:bottomBar.copyNodesModal";
const mapId = "copyNodesMap";

enum AppendType {
  Start = "start",
  End = "end",
  Custom = "custom",
}

enum SubmissionStep {
  Copying = "copying",
  Deleting = "deleting",
}

export const CopyNodesModal: React.FC<{
  onClose: () => void;
}> = observer(({ onClose }) => {
  const {
    projectStore,
    campaignStore,
    osaWayStore,
    osaNodeStore,
    codingStore,
  } = useStores();

  const [map, setMap] = useState<mapboxgl.Map>();
  const [keepOriginal, setKeepOriginal] = useState(true);
  const [appendType, setAppendType] = useState(AppendType.Start);
  const [appendIndex, setAppendIndex] = useState(0);
  const [submissionStep, setSubmissionStep] = useState<SubmissionStep>();
  const [submissionProgress, setSubmissionProgress] = useState<ProgressState>();

  const [projects, setProjects] = useState<Project[]>();
  const [selectedProject, setSelectedProject] = useState<Project>();

  const [campaigns, setCampaigns] = useState<Campaign[]>();
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>();

  const [osaWays, setOsaWays] = useState<OsaWay[]>();
  const [selectedOsaWay, setSelectedOsaWay] = useState<OsaWay>();

  const originWay = osaWayStore.getSelected()!;
  const selectedNodes = osaNodeStore.getMultiSelectedNodesByOsaWay(
    originWay.id,
  );

  // use this to avoid crashes when selecting a different route
  const resetAppendState = () => {
    setAppendType(AppendType.Start);
    setAppendIndex(0);
  };

  const targetIsEmptyOrNull = () =>
    !selectedOsaWay?.geoJson ||
    (selectedOsaWay?.geoJson && !selectedOsaWay.geoJson.features.length);

  const getSortedSelectedNodes = () =>
    selectedNodes.sort((a, b) => a.index - b.index);

  const geoJson = useMemo<FeatureCollection>(() => {
    const buildConnectionLineFeature = (
      from: Position,
      to: Position,
    ): Feature => ({
      type: "Feature",
      properties: {
        stroke: "#00a498",
        line: "#ffffff",
      },
      geometry: {
        type: "LineString",
        coordinates: [from, to],
      },
    });

    const getConnectionLines = (
      targetFeatures: Feature[],
      nodes: OsaNode[],
    ): Feature[] => {
      const targetPoints = targetFeatures
        .map((feat) => feat.geometry)
        .filter((geometry) => geometry.type === "Point");

      const lineFeatures: Feature[] = [];

      if (appendIndex > 0) {
        // add line that connects the first node to the previous feature
        const previousPoint = targetPoints[appendIndex - 1];
        const from: Position = previousPoint.coordinates;
        const firstNode = nodes[0];
        const to: Position = [firstNode.longitude, firstNode.latitude];
        lineFeatures.push(buildConnectionLineFeature(from, to));
      }

      if (appendIndex < targetPoints.length) {
        // add line that connects the last node to the next feature
        const lastNode = nodes[nodes.length - 1];
        const from: Position = [lastNode.longitude, lastNode.latitude];
        const nextPoint = targetPoints[appendIndex];
        const to: Position = nextPoint.coordinates;
        lineFeatures.push(buildConnectionLineFeature(from, to));
      }

      return lineFeatures;
    };

    var features: Feature[] = [];

    const sortedNodes = getSortedSelectedNodes();

    if (selectedOsaWay?.geoJson) {
      // add target data collection
      features.push({
        type: "Feature",

        properties: {
          stroke: "#000000",
          line: "#ffffff",
        },
        geometry: {
          type: "LineString",
          coordinates: selectedOsaWay.geoJson.features
            .filter((feature) => feature.geometry.type === "Point")
            .map((feature) => feature.geometry as Point)
            .map((lineString) => lineString.coordinates),
        },
      });

      // add connection between copied nodes and the target data collection
      const targetPointFeatures = selectedOsaWay.geoJson.features.filter(
        (feat) => feat.geometry.type === "Point",
      );
      const connectionLines = getConnectionLines(
        targetPointFeatures,
        sortedNodes,
      );

      features.push(...connectionLines);
    }

    // add copied nodes
    sortedNodes.forEach((node, index, list) => {
      const isLastItem = index + 1 === list.length;

      if (!isLastItem) {
        const nextNode = list[index + 1];
        features.push({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [
              [node.longitude, node.latitude],
              [nextNode.longitude, nextNode.latitude],
            ],
          },
          properties: {
            stroke: "#00a498",
            line: "#ffffff",
          },
        });
      }
    });

    return {
      type: "FeatureCollection",
      features,
    };

    // eslint-disable-next-line
  }, [selectedOsaWay, appendType, appendIndex, map]);

  useEffect(() => {
    const newMap = new mapboxgl.Map({
      ...mapUtils.defaultConfig,
      container: mapId,
    });
    newMap.on("load", () => {
      newMap.resize();
      copyNodesMapSource.config(newMap);

      newMap.addControl(new mapboxgl.NavigationControl());
      newMap.addControl(
        new mapboxgl.ScaleControl({
          maxWidth: 100,
          unit: "metric",
        }),
      );
      setMap(newMap);
    });

    return () => {
      newMap.remove();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (mapActive(map)) {
      mapUtils.centerMap(map!, (bounds) => {
        copyNodesMapSource.extendBounds(bounds, geoJson);
      });
    }
    // eslint-disable-next-line
  }, [selectedOsaWay, map]);

  useEffect(() => {
    if (mapActive(map)) {
      copyNodesMapSource.update(map!, geoJson);
    }
    // eslint-disable-next-line
  }, [geoJson]);

  useEffect(() => {
    const preSelectedProject = projectStore.getSelected();
    if (preSelectedProject) setSelectedProject(preSelectedProject);
    setProjects(projectStore.projects);

    const preSelectedCampaign = campaignStore.selected;
    if (preSelectedCampaign) {
      setSelectedCampaign(preSelectedCampaign);
      setSelectedOsaWay(originWay);
      resetAppendState();
    }

    compileAllPages(
      async (pageNumber) => await ProjectApi.page(20, pageNumber),
    ).then((projects) => {
      setProjects(projects);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedProject) {
      ProjectApi.pageCampaigns(selectedProject.id, 200, 0).then(
        ({ data: page }) => {
          setCampaigns(page.data);
          if (page.data.length === 1) {
            setSelectedCampaign(page.data[0]);
          }
        },
      );
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedCampaign && selectedProject) {
      CampaignApi.pageOsaWays(
        selectedCampaign.id,
        [OptionalScope.GeoJson, OptionalScope.Stats],
        200,
        0,
      ).then(({ data: page }) => {
        setOsaWays(page.data);
        if (page.data.length === 1) {
          setSelectedOsaWay(page.data[0]);
          resetAppendState();
        }
      });
    }
    // eslint-disable-next-line
  }, [selectedCampaign]);

  useEffect(() => {
    if (
      selectedOsaWay &&
      appendType === AppendType.End &&
      targetIsEmptyOrNull()
    ) {
      setAppendType(AppendType.Start);
    }
    // eslint-disable-next-line
  }, [selectedOsaWay]);

  const onSubmit = async () => {
    setSubmissionStep(SubmissionStep.Copying);
    const targetOsaWay = selectedOsaWay;

    const sortedNodes = getSortedSelectedNodes();

    var pasteAtIndex = 0;
    const targetGeoJson = targetOsaWay!.geoJson;
    if (appendIndex > 0 && targetGeoJson?.features.length) {
      const firstDisplacedFeature =
        targetGeoJson.features[(appendIndex - 1) * 2];
      const { id } = firstDisplacedFeature.properties as GeoJsonNodeProps;

      const { data: firstDisplacedNode } = await OsaNodeApi.get(id, []);

      pasteAtIndex = firstDisplacedNode.index + 1;
    }

    const onCopySuccess = async () => {
      if (!keepOriginal) {
        setSubmissionStep(SubmissionStep.Deleting);
        const totalNodes = osaNodeStore.multiSelectedIds.size;
        var deletedNodes = 0;

        await osaNodeStore.deleteSelectedNodes(() => {
          deletedNodes++;
          setSubmissionProgress({ value: deletedNodes, total: totalNodes });
        });
        await osaWayStore.compileOsaWayNodes(originWay.id);
      }
      const targetOsaWayNeedsRefresh = osaWayStore.ways.find(
        (w) => w.id === targetOsaWay!.id,
      );
      if (targetOsaWayNeedsRefresh) {
        await osaWayStore.refreshOsaWay(targetOsaWay!.id);
        await osaNodeStore.fetchNodes(targetOsaWay!.id);
        await codingStore.fetchAttributesByOsaWayId(targetOsaWay!.id);
      }
      toast("success", t(`${i18prefix}.toast.success`));

      onClose();
    };

    await OsaWayApi.copyNodes(originWay.id, {
      targetOsaWayId: targetOsaWay!.id,
      pasteAtIndex,
      indexes: sortedNodes.map((node) => node.index),
    })
      .then(async ({ data: jobStateId }) => {
        await JobStateApi.poll(jobStateId, 1000, (jobState) => {
          setSubmissionProgress({
            value: jobState.progressValue,
            total: jobState.progressTotal,
          });
        });

        await onCopySuccess();
      })
      .catch(() => {
        toast("error", t(`${i18prefix}.toast.error`));
      })
      .finally(() => {
        setSubmissionStep(undefined);
      });
  };

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header={t(`${i18prefix}.title`)}
      footer={
        <HStack flexGrow={1}>
          {!!submissionStep && (
            <ProgressBar
              submissionProgress={submissionProgress}
              submissionStep={submissionStep}
            />
          )}

          <Spacer />
          <Button
            colorPalette={"primary"}
            loading={!!submissionStep}
            onClick={onSubmit}
            disabled={!selectedOsaWay}
          >
            {t(`${i18prefix}.submit`)}
          </Button>
        </HStack>
      }
    >
      <HStack alignItems="stretch" gap={6}>
        <Stack width={"340px"} gap={6}>
          <Stack>
            <Text fontSize={"xs"} textTransform="uppercase">
              {t(`${i18prefix}.destination`)}
            </Text>

            <NativeSelect.Root disabled={!projects}>
              <NativeSelect.Field
                placeholder={t(`${i18prefix}.selectProject`)}
                value={selectedProject?.id ?? ""}
                onChange={(e) => {
                  const projectId = e.target.value;
                  const project = projects?.find((p) => p.id === projectId);
                  setSelectedProject(project);
                  setSelectedCampaign(undefined);
                  setCampaigns(undefined);
                }}
              >
                {projects?.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.city}: {project.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            <NativeSelect.Root disabled={!selectedProject || !campaigns}>
              <NativeSelect.Field
                placeholder={t(`${i18prefix}.selectCampaign`)}
                value={selectedCampaign?.id ?? ""}
                onChange={(e) => {
                  const campaignId = e.target.value;
                  const campaign = campaigns?.find((c) => c.id === campaignId);
                  setSelectedCampaign(campaign);
                  setSelectedOsaWay(undefined);
                  setOsaWays(undefined);
                }}
              >
                {campaigns?.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            <NativeSelect.Root disabled={!selectedCampaign || !osaWays}>
              <NativeSelect.Field
                placeholder={t(`${i18prefix}.selectRoute`)}
                value={selectedOsaWay?.id ?? ""}
                onChange={(e) => {
                  const osaWayId = e.target.value;
                  const osaWay = osaWays?.find((c) => c.id === osaWayId);
                  setSelectedOsaWay(osaWay);
                  resetAppendState();
                }}
              >
                {osaWays?.map((way) => (
                  <option key={way.id} value={way.id}>
                    {way.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            {!!selectedOsaWay && (
              <Alert.Root
                overflow="hidden"
                status={
                  selectedOsaWay?.permissions.canCreate ? "info" : "error"
                }
                py={2}
                px={3}
              >
                <Alert.Indicator />
                <Alert.Title fontWeight={"normal"} fontSize="sm">
                  {selectedOsaWay?.permissions.canCreate
                    ? selectedOsaWay.stats?.nNodes
                      ? selectedOsaWay.id === originWay.id
                        ? t(`${i18prefix}.alert.sameOsaWay`)
                        : t(`${i18prefix}.alert.hasNodes`)
                      : t(`${i18prefix}.alert.isEmpty`)
                    : t(`${i18prefix}.alert.noPermissions`)}
                </Alert.Title>
              </Alert.Root>
            )}
          </Stack>

          <Checkbox.Root
            colorPalette="primary"
            checked={keepOriginal}
            onCheckedChange={({ checked }) => {
              setKeepOriginal(!!checked);
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>{t(`${i18prefix}.keepOriginal`)}</Checkbox.Label>
          </Checkbox.Root>

          <Stack>
            <Text>{t(`${i18prefix}.appendTo`)}</Text>

            <RadioGroup.Root
              colorPalette="primary"
              value={appendType}
              onValueChange={({ value }) => {
                const appendType = value as AppendType;
                setAppendType(appendType);
                if (appendType === AppendType.Start) {
                  setAppendIndex(0);
                } else if (appendType === AppendType.End) {
                  setAppendIndex(selectedOsaWay!.stats!.nNodes);
                }
              }}
            >
              <HStack gap={6}>
                <AppendTypeRadioItem appendType={AppendType.Start} />
                <AppendTypeRadioItem
                  appendType={AppendType.End}
                  disabled={targetIsEmptyOrNull()}
                />
                <AppendTypeRadioItem
                  appendType={AppendType.Custom}
                  disabled={targetIsEmptyOrNull()}
                />
              </HStack>
            </RadioGroup.Root>

            {appendType === AppendType.Custom && (
              <Field.Root>
                <Input
                  type="number"
                  value={appendIndex}
                  onChange={(e) => {
                    const intValue = parseInt(e.target.value || "0");

                    if (
                      selectedOsaWay &&
                      intValue >= 0 &&
                      intValue <= selectedOsaWay!.stats!.nNodes
                    ) {
                      setAppendIndex(intValue);
                    }
                  }}
                ></Input>
              </Field.Root>
            )}
          </Stack>
        </Stack>

        <Box id={mapId} flexGrow={1} height="460px" />
      </HStack>
    </CustomDialog>
  );
});

const AppendTypeRadioItem = ({
  appendType,
  disabled = false,
}: {
  appendType: AppendType;
  disabled?: boolean;
}) => {
  return (
    <RadioGroup.Item value={appendType} disabled={disabled}>
      <RadioGroup.ItemHiddenInput />
      <RadioGroup.ItemIndicator />
      <RadioGroup.ItemText>
        {t(`${i18prefix}.appendType.${appendType}`)}
      </RadioGroup.ItemText>
    </RadioGroup.Item>
  );
};

export default AppendTypeRadioItem;

const ProgressBar = ({
  submissionStep,
  submissionProgress,
}: {
  submissionStep: SubmissionStep;
  submissionProgress: ProgressState | undefined;
}) => {
  return (
    <Progress.Root
      width={"200px"}
      size={"md"}
      value={submissionProgress?.value ?? null}
      max={submissionProgress?.total}
      striped
      animated
      colorPalette={
        submissionStep === SubmissionStep.Copying ? "primary" : "red"
      }
    >
      <Progress.Track>
        <Progress.Range />
      </Progress.Track>
      {!!submissionProgress && (
        <Progress.ValueText>
          {t(`${i18prefix}.progress.${submissionStep}`)}
        </Progress.ValueText>
      )}
    </Progress.Root>
  );
};
